import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { RegeneratePersonaOpportunitySetMode } from "../../../api/persona-opportunity-sets.api.js";

const REGENERATED_SUFFIX = " (regenerated)";

/** Prefill for `new_set` — strips one trailing ` (regenerated)` so re-regenerate does not stack. */
export function defaultRegeneratedSetName(sourceName: string): string {
  const baseName = sourceName.endsWith(REGENERATED_SUFFIX)
    ? sourceName.slice(0, -REGENERATED_SUFFIX.length)
    : sourceName;
  return `${baseName}${REGENERATED_SUFFIX}`;
}

export interface RegeneratePersonaOpportunitySetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceName: string;
  isSubmitting?: boolean;
  onConfirm: (input: { mode: RegeneratePersonaOpportunitySetMode; name?: string }) => void;
}

export function RegeneratePersonaOpportunitySetDialog({
  open,
  onOpenChange,
  sourceName,
  isSubmitting,
  onConfirm,
}: RegeneratePersonaOpportunitySetDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = React.useState<RegeneratePersonaOpportunitySetMode>("overwrite");
  const [name, setName] = React.useState(() => defaultRegeneratedSetName(sourceName));

  React.useEffect(() => {
    if (!open) return;
    setMode("overwrite");
    setName(defaultRegeneratedSetName(sourceName));
  }, [open, sourceName]);

  const canSubmit = mode === "overwrite" || name.trim().length >= 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("personaOpportunities.regenerateDialog.title", "Regenerate set")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "personaOpportunities.regenerateDialog.description",
              "Overwrite this set’s opportunities, or create a new set with the same settings.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="persona-regen-mode"
              className="mt-1"
              checked={mode === "overwrite"}
              onChange={() => setMode("overwrite")}
            />
            <span>
              {t(
                "personaOpportunities.regenerateDialog.overwrite",
                "Overwrite existing set",
              )}
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="persona-regen-mode"
              className="mt-1"
              checked={mode === "new_set"}
              onChange={() => setMode("new_set")}
            />
            <span>
              {t("personaOpportunities.regenerateDialog.newSet", "Generate new set")}
            </span>
          </label>

          {mode === "new_set" ? (
            <div className="space-y-1.5 pl-6">
              <Label htmlFor="persona-regen-name">
                {t("personaOpportunities.regenerateDialog.nameLabel", "New set name")}
              </Label>
              <Input
                id="persona-regen-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={isSubmitting}
              />
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || isSubmitting}
            onClick={() =>
              onConfirm(
                mode === "new_set"
                  ? { mode, name: name.trim() }
                  : { mode: "overwrite" },
              )
            }
          >
            {isSubmitting
              ? t("common.creating", "Creating…")
              : t("personaOpportunities.workspace.regenerate", "Regenerate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
